import React, { useState } from 'react';
import { Card, Button, Alert, Spin } from 'antd';
import { EditOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useSignUp } from '../context/SignUpContext';
import StepNavigation from '../components/StepNavigation';
import { createSeller } from '../../../../api/signup';
import { bankOptions } from '../../../../utils/bankList';

const StepReview = () => {
  const { formData, buildSellerPayload, goToStep, nextStep, prevStep } = useSignUp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isPJ = formData.accountType === 'SELLER_PJ';

  // Format CPF for display
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  // Format CNPJ for display
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  // Format phone for display
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').replace(/^55/, '');
    if (numbers.length === 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  };

  // Format CEP for display
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  // Format currency for display
  const formatCurrency = (value) => {
    const amount = parseInt(value, 10);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Get bank name by code
  const getBankName = (code) => {
    const bank = bankOptions.find(b => b.value === code);
    return bank ? bank.label : code;
  };

  // Get account type label
  const getAccountTypeLabel = (type) => {
    return type === 'CHECKING_ACCOUNT' ? 'Conta Corrente' : 'Conta Poupança';
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = buildSellerPayload();
      await createSeller(payload);

      nextStep(); // Go to success step
    } catch (err) {
      setLoading(false);

      // Parse API error
      if (err.message) {
        const errorMessages = [];
        Object.entries(err.message).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            errorMessages.push(`${key}: ${value.join(', ')}`);
          } else if (typeof value === 'object') {
            Object.entries(value).forEach(([subKey, subValue]) => {
              errorMessages.push(`${key}.${subKey}: ${subValue}`);
            });
          } else {
            errorMessages.push(`${key}: ${value}`);
          }
        });
        setError(errorMessages.join('\n'));
      } else {
        setError('Ocorreu um erro ao criar sua conta. Tente novamente.');
      }
    }
  };

  const ReviewSection = ({ title, editStep, children }) => (
    <Card
      className="review-section"
      title={title}
      extra={
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => goToStep(editStep)}
          disabled={loading}
        >
          Editar
        </Button>
      }
    >
      {children}
    </Card>
  );

  const ReviewItem = ({ label, value }) => (
    <div className="review-item">
      <span className="review-label">{label}</span>
      <span className="review-value">{value || '-'}</span>
    </div>
  );

  return (
    <div className="step-content step-review">
      <div className="step-header">
        <h2>Revise seus dados</h2>
        <p>Confira se todas as informações estão corretas antes de finalizar.</p>
      </div>

      {error && (
        <Alert
          message="Erro ao criar conta"
          description={<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error}</pre>}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      <ReviewSection title="Tipo de conta" editStep={1}>
        <ReviewItem
          label="Tipo"
          value={isPJ ? 'Pessoa Jurídica (CNPJ)' : 'Pessoa Física (CPF)'}
        />
      </ReviewSection>

      <ReviewSection title="Dados pessoais" editStep={2}>
        <ReviewItem label="Nome" value={`${formData.person.first_name} ${formData.person.last_name}`} />
        <ReviewItem label="E-mail" value={formData.person.email} />
        <ReviewItem label="Telefone" value={formatPhone(formData.person.phone)} />
        <ReviewItem label="CPF" value={formatCPF(formData.person.taxpayer_id)} />
        <ReviewItem label="Data de nascimento" value={formData.person.birthdate ? new Date(formData.person.birthdate).toLocaleDateString('pt-BR') : ''} />
        <ReviewItem label="Nome da mãe" value={formData.person.mother_name} />
      </ReviewSection>

      <ReviewSection title="Endereço" editStep={3}>
        <ReviewItem
          label="Endereço"
          value={`${formData.person.address.street}${formData.person.address.number ? `, ${formData.person.address.number}` : ''}${formData.person.address.complement ? ` - ${formData.person.address.complement}` : ''}`}
        />
        <ReviewItem label="Bairro" value={formData.person.address.neighborhood} />
        <ReviewItem label="Cidade/UF" value={`${formData.person.address.city} - ${formData.person.address.state}`} />
        <ReviewItem label="CEP" value={formatCEP(formData.person.address.postal_code)} />
      </ReviewSection>

      <ReviewSection title="Dados do negócio" editStep={4}>
        <ReviewItem label="Categoria" value={formData.categoryLabel || formData.category} />
        <ReviewItem label="MCC" value={formData.mccLabel || formData.mcc} />
        <ReviewItem label="Nome na fatura" value={formData.statement_descriptor} />
        <ReviewItem label="Faturamento esperado" value={formatCurrency(formData.revenue)} />
      </ReviewSection>

      <ReviewSection title="Conta bancária" editStep={5}>
        <ReviewItem label="Banco" value={getBankName(formData.bank_account.code)} />
        <ReviewItem label="Agência" value={formData.bank_account.agency} />
        <ReviewItem label="Conta" value={formData.bank_account.number} />
        <ReviewItem label="Tipo" value={getAccountTypeLabel(formData.bank_account.type)} />
      </ReviewSection>

      {isPJ && (
        <ReviewSection title="Dados da empresa" editStep={6}>
          <ReviewItem label="Razão social" value={formData.company.name} />
          <ReviewItem label="CNPJ" value={formatCNPJ(formData.company.tax_id)} />
          <ReviewItem label="Data de Abertura" value={formData.company.opening_date ? new Date(`${formData.company.opening_date}T00:00:00`).toLocaleDateString('pt-BR') : ''} />
          <ReviewItem label="E-mail" value={formData.company.email} />
          <ReviewItem label="Telefone" value={formatPhone(formData.company.phone)} />
          <ReviewItem
            label="Endereço"
            value={`${formData.company.address.street}${formData.company.address.number ? `, ${formData.company.address.number}` : ''}${formData.company.address.complement ? ` - ${formData.company.address.complement}` : ''}`}
          />
          <ReviewItem label="Bairro" value={formData.company.address.neighborhood} />
          <ReviewItem label="Cidade/UF" value={`${formData.company.address.city} - ${formData.company.address.state}`} />
          <ReviewItem label="CEP" value={formatCEP(formData.company.address.postal_code)} />
        </ReviewSection>
      )}

      <div className="review-confirmation">
        <CheckCircleOutlined />
        <p>Ao clicar em "Finalizar cadastro", você concorda com nossos termos de uso e política de privacidade.</p>
      </div>

      <StepNavigation
        onNext={handleSubmit}
        onBack={prevStep}
        nextText={loading ? 'Finalizando...' : 'Finalizar cadastro'}
        loading={loading}
      />
    </div>
  );
};

export default StepReview;
